import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { PageLoader } from "@/components/ui/page-loader";

import { salesApi } from "../client.js";
import type { DialogContact } from "./ContactDialog.js";
import { EmailStatusBadge } from "./EmailStatusBadge.js";

/** Human labels for the raw harvester status / lead-promotion slugs, so the
 * dialog reads in English instead of `contacts_done` / `pending`. */
const STATUS_LABELS: Record<string, string> = {
  pending: "Queued",
  enumerated: "Found",
  contacts_pending: "Digging contacts",
  contacts_done: "Contacts dug",
  done: "Complete",
};
const LEAD_LABELS: Record<string, string> = {
  none: "Not a lead",
  pending: "Lead pending",
  promoted: "Promoted to lead",
  rejected: "Rejected",
};

/** An organisation opened as a dialog: its profile + EVERY contact harvested for
 * it. As the harvester re-digs the org over time, this fills out further. Click
 * a contact row to open it. */
export function OrgDialog({
  orgId,
  onClose,
  onOpenContact,
}: {
  orgId: string | null;
  onClose: () => void;
  onOpenContact: (c: DialogContact) => void;
}): JSX.Element {
  const org = useQuery({
    queryKey: ["sales", "harvest", "org", orgId],
    queryFn: () => salesApi.getHarvestOrg(orgId!),
    enabled: Boolean(orgId),
  });

  const d = org.data;
  const statusLabel = d ? STATUS_LABELS[d.status] ?? d.status : "";
  const leadLabel = d ? LEAD_LABELS[d.promotion_state] ?? d.promotion_state : "";

  return (
    <Dialog open={Boolean(orgId)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle component="div" sx={{ pr: 6 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
          {d?.name ?? "Organisation"}
        </Typography>
        {d?.domain && (
          <Link
            href={`https://${d.domain}`}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{
              mt: 0.25,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 13,
              color: "primary.main",
              fontWeight: 500,
            }}
          >
            {d.domain}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        )}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 12, right: 12, color: "text.secondary" }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {org.isLoading || !d ? (
          <PageLoader />
        ) : (
          <Stack spacing={2.5}>
            {/* At-a-glance chips: industry / status / lead */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={d.vertical}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 1, fontWeight: 500 }}
              />
              <Chip label={statusLabel} size="small" sx={{ borderRadius: 1 }} />
              <Chip
                label={leadLabel}
                size="small"
                color={d.promotion_state === "promoted" ? "success" : "default"}
                variant={d.promotion_state === "promoted" ? "filled" : "outlined"}
                sx={{ borderRadius: 1 }}
              />
            </Stack>

            {/* Profile facts — a clean definition grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                rowGap: 1.25,
                columnGap: 3,
                p: 2,
                borderRadius: 1,
                bgcolor: "surfaceSoft",
              }}
            >
              <Info label="Country" value={d.country || "—"} />
              <Info label="Region" value={d.region || "—"} />
              <Info label="Phone" value={d.phone || "—"} />
              <Info label="Website" value={d.domain || "—"} />
              {d.address ? <Info label="Address" value={d.address} span /> : null}
            </Box>

            {/* Contacts */}
            <Box>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Contacts ({d.contacts.length})
                </Typography>
                {d.contacts.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Click a row for full detail
                  </Typography>
                )}
              </Stack>
              {d.contacts.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    borderStyle: "dashed",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No contacts dug yet — the harvester fills this in as it digs and re-visits the org.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { bgcolor: "surfaceSoft", fontWeight: 600, color: "text.secondary" } }}>
                        <TableCell>Name &amp; role</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {d.contacts.map((c) => (
                        <TableRow
                          key={c.id}
                          hover
                          sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}
                          onClick={() => onOpenContact({ ...c, org_name: d.name, org_id: d.id })}
                        >
                          <TableCell sx={{ verticalAlign: "top" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.full_name}</Typography>
                            <Chip
                              label={c.seniority}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5, borderRadius: 1, height: 18, fontSize: 10, textTransform: "capitalize" }}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", color: "text.secondary" }}>
                            {c.title || "—"}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            {c.email ? (
                              <Stack spacing={0.5} alignItems="flex-start">
                                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{c.email}</Typography>
                                <EmailStatusBadge contact={c} />
                              </Stack>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>{c.phone || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, span }: { label: string; value: string; span?: boolean }): JSX.Element {
  return (
    <Box sx={{ gridColumn: span ? "1 / -1" : "auto" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word", fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}
